import { ensureBMapGL } from './loadBMapGL';
import { wgs84ToBd09 } from './coordBd09Wgs';

/**
 * 浏览器定位：优先天地图 T.Geolocation；脚本未就绪时立刻走 HTML5 弹出授权。
 * 坐标取 CGCS2000/WGS84，再转为库内 BD09；逆地理走天地图 Geocoder。
 */

export const BAIDU_GEO_STATUS = {
  SUCCESS: 0,
  CITY_LIST: 1,
  UNKNOWN_LOCATION: 2,
  UNKNOWN_ROUTE: 3,
  INVALID_KEY: 4,
  INVALID_REQUEST: 5,
  PERMISSION_DENIED: 6,
  SERVICE_UNAVAILABLE: 7,
  TIMEOUT: 8,
};

export function mapBaiduGeoStatus(status) {
  const code = Number(status);
  if (code === BAIDU_GEO_STATUS.SUCCESS) {
    return { code: 'SUCCESS', message: '定位成功', permissionDenied: false };
  }
  if (code === BAIDU_GEO_STATUS.PERMISSION_DENIED) {
    return {
      code: 'PERMISSION_DENIED',
      message: '定位权限被拒绝，请在浏览器地址栏允许「位置」，或手动选择位置',
      permissionDenied: true,
    };
  }
  if (code === BAIDU_GEO_STATUS.TIMEOUT) {
    return {
      code: 'TIMEOUT',
      message: '定位超时，请重试或手动选择位置',
      permissionDenied: false,
    };
  }
  if (code === BAIDU_GEO_STATUS.UNKNOWN_LOCATION) {
    return {
      code: 'UNAVAILABLE',
      message: '位置信息不可用，请检查网络后重试，或手动选择位置',
      permissionDenied: false,
    };
  }
  if (code === BAIDU_GEO_STATUS.INVALID_KEY) {
    return {
      code: 'INVALID_KEY',
      message: '地图密钥无效，请联系管理员',
      permissionDenied: false,
    };
  }
  return {
    code: 'FAILED',
    message: `定位失败（状态码 ${Number.isFinite(code) ? code : '未知'}），请重试或手动选择位置`,
    permissionDenied: false,
  };
}

function rejectMapped(mapped) {
  return Object.assign(new Error(mapped.message), mapped);
}

function pickAddressFromGeocoder(rs) {
  const ac = rs?.addressComponents || {};
  const province = ac.province || '';
  const city = ac.city || ac.province || '';
  const district = ac.district || '';
  const street = ac.street || '';
  const streetNumber = ac.streetNumber || '';
  const formatted = rs?.address || [province, city, district, street, streetNumber].filter(Boolean).join('');
  if (!formatted) return null;
  return {
    province,
    city,
    district,
    township: '',
    street,
    streetNumber,
    formattedAddress: formatted,
    source: '天地图逆地理',
  };
}

function readTdtLngLat(lnglat) {
  if (!lnglat) return null;
  const lng = typeof lnglat.getLng === 'function' ? Number(lnglat.getLng()) : Number(lnglat.lng ?? lnglat.lon);
  const lat = typeof lnglat.getLat === 'function' ? Number(lnglat.getLat()) : Number(lnglat.lat);
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;
  return { lng, lat };
}

/**
 * 天地图官方定位。必须在用户手势/首屏同步调用链中启动（不可先 await），否则 Chrome 不弹授权。
 * 成功状态：移动端多为 0，PC 端多为 1。
 */
export function requestTiandituGeolocation(opts = {}) {
  const { timeout = 10000 } = opts;
  return new Promise((resolve, reject) => {
    const Tapi = window.T;
    if (!Tapi?.Geolocation) {
      reject(
        Object.assign(new Error('天地图定位组件未就绪'), {
          code: 'UNAVAILABLE',
          permissionDenied: false,
        })
      );
      return;
    }

    const isSecure =
      typeof window.isSecureContext === 'boolean'
        ? window.isSecureContext
        : window.location.protocol === 'https:' ||
          window.location.hostname === 'localhost' ||
          window.location.hostname === '127.0.0.1';
    if (!isSecure) {
      reject(
        Object.assign(
          new Error('当前页面非 HTTPS，浏览器禁止定位。请使用 https 或 localhost 访问'),
          { code: 'INSECURE_CONTEXT', permissionDenied: false }
        )
      );
      return;
    }

    let settled = false;
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      reject(rejectMapped(mapBaiduGeoStatus(BAIDU_GEO_STATUS.TIMEOUT)));
    }, Math.max(Number(timeout) || 10000, 5000));

    const finish = (fn) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      fn();
    };

    try {
      const geo = new Tapi.Geolocation();
      const onPos = function onPos(e) {
        const status = Number(typeof this?.getStatus === 'function' ? this.getStatus() : e?.status);
        const ll = readTdtLngLat(e?.lnglat || e?.lngLat);
        if (ll && (status === 0 || status === 1)) {
          finish(() =>
            resolve({
              latitude: ll.lat,
              longitude: ll.lng,
              accuracy: e?.accuracy != null ? Number(e.accuracy) : null,
              address: null,
              source: 'tianditu',
              coordType: 'wgs84',
            })
          );
          return;
        }
        if (status === 1 || status === 6) {
          finish(() => reject(rejectMapped(mapBaiduGeoStatus(BAIDU_GEO_STATUS.PERMISSION_DENIED))));
          return;
        }
        finish(() => reject(rejectMapped(mapBaiduGeoStatus(BAIDU_GEO_STATUS.UNKNOWN_LOCATION))));
      };
      try {
        geo.getCurrentPosition(onPos, { enableHighAccuracy: true, timeout, maximumAge: 0 });
      } catch {
        geo.getCurrentPosition(onPos);
      }
    } catch (err) {
      finish(() =>
        reject(
          Object.assign(new Error(err?.message || '天地图定位失败'), {
            code: 'FAILED',
            permissionDenied: false,
          })
        )
      );
    }
  });
}

/**
 * HTML5 定位：必须在用户点击同步调用链中启动（不可先 await），否则 Chrome 不弹授权。
 */
export function requestHtml5Geolocation(opts = {}) {
  const { enableHighAccuracy = true, timeout = 10000, maximumAge = 0 } = opts;

  return new Promise((resolve, reject) => {
    if (!navigator?.geolocation) {
      reject(
        Object.assign(new Error('当前浏览器不支持定位，请换用 Chrome/Edge，或手动选择位置'), {
          code: 'NO_GEOLOCATION',
          permissionDenied: false,
        })
      );
      return;
    }

    const isSecure =
      typeof window.isSecureContext === 'boolean'
        ? window.isSecureContext
        : window.location.protocol === 'https:' ||
          window.location.hostname === 'localhost' ||
          window.location.hostname === '127.0.0.1';
    if (!isSecure) {
      reject(
        Object.assign(
          new Error('当前页面非 HTTPS，浏览器禁止定位。请使用 https 或 localhost 访问'),
          { code: 'INSECURE_CONTEXT', permissionDenied: false }
        )
      );
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const latitude = Number(pos.coords.latitude);
        const longitude = Number(pos.coords.longitude);
        if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
          reject(rejectMapped(mapBaiduGeoStatus(BAIDU_GEO_STATUS.UNKNOWN_LOCATION)));
          return;
        }
        resolve({
          latitude,
          longitude,
          accuracy: pos.coords.accuracy != null ? Number(pos.coords.accuracy) : null,
          address: null,
          source: 'html5',
          coordType: 'wgs84',
        });
      },
      (err) => {
        const code = err?.code;
        if (code === 1) {
          reject(rejectMapped(mapBaiduGeoStatus(BAIDU_GEO_STATUS.PERMISSION_DENIED)));
          return;
        }
        if (code === 3) {
          reject(rejectMapped(mapBaiduGeoStatus(BAIDU_GEO_STATUS.TIMEOUT)));
          return;
        }
        if (code === 2) {
          reject(rejectMapped(mapBaiduGeoStatus(BAIDU_GEO_STATUS.UNKNOWN_LOCATION)));
          return;
        }
        reject(
          Object.assign(new Error(err?.message || '浏览器定位失败'), {
            code: 'FAILED',
            permissionDenied: false,
          })
        );
      },
      { enableHighAccuracy, timeout, maximumAge }
    );
  });
}

function reverseGeocodeBd09(longitude, latitude) {
  return new Promise((resolve) => {
    try {
      const BMapGL = window.BMapGL;
      if (!BMapGL?.Geocoder || !BMapGL?.Point) {
        resolve(null);
        return;
      }
      const geocoder = new BMapGL.Geocoder();
      geocoder.getLocation(new BMapGL.Point(longitude, latitude), (rs) => {
        resolve(pickAddressFromGeocoder(rs));
      });
    } catch {
      resolve(null);
    }
  });
}

function startLocateImmediately(opts = {}) {
  const { enableHighAccuracy = true, timeout = 10000, maximumAge = 0, html5Promise: injectedHtml5 } = opts;
  if (injectedHtml5) return { promise: injectedHtml5, via: 'injected' };
  if (window.T?.Geolocation) {
    return { promise: requestTiandituGeolocation({ timeout }), via: 'tianditu' };
  }
  return {
    promise: requestHtml5Geolocation({ enableHighAccuracy, timeout, maximumAge }),
    via: 'html5',
  };
}

export async function getBaiduBrowserPosition(opts = {}) {
  const {
    enableHighAccuracy = true,
    timeout = 10000,
    maximumAge = 0,
    loadTimeoutMs = 15000,
    html5Promise: injectedHtml5,
  } = opts;

  // 不可先 await 脚本：天地图已就绪则立刻 T.Geolocation，否则立刻 HTML5，保证 Chrome 弹出授权。
  const started = startLocateImmediately({
    enableHighAccuracy,
    timeout,
    maximumAge,
    html5Promise: injectedHtml5,
  });
  const mapPromise = ensureBMapGL({ timeoutMs: loadTimeoutMs }).catch(() => null);

  let locateResult = null;
  let locateError = null;
  try {
    locateResult = await started.promise;
  } catch (err) {
    locateError = err;
  }

  if (!locateResult?.latitude && !locateError?.permissionDenied && !injectedHtml5) {
    await mapPromise;
    if (window.T?.Geolocation && started.via !== 'tianditu') {
      try {
        locateResult = await requestTiandituGeolocation({ timeout });
        locateError = null;
      } catch (err) {
        locateError = locateError || err;
      }
    }
  }

  if (!locateResult?.latitude) {
    if (locateError?.permissionDenied) throw locateError;
    throw (
      locateError ||
      Object.assign(new Error('定位失败，请允许浏览器位置权限后重试'), {
        code: 'FAILED',
        permissionDenied: false,
      })
    );
  }

  const bd = wgs84ToBd09(locateResult.longitude, locateResult.latitude);
  await mapPromise;
  const address = await reverseGeocodeBd09(bd.lng, bd.lat);

  return {
    ...locateResult,
    latitude: bd.lat,
    longitude: bd.lng,
    address: address || locateResult.address,
    source: locateResult.source === 'tianditu' ? 'tianditu-bd09' : 'html5-bd09',
    coordType: 'bd09',
  };
}

export async function getBaiduCurrentPosition(opts = {}) {
  return getBaiduBrowserPosition(opts);
}

export async function getCurrentPositionViaBaidu(opts = {}) {
  return getBaiduBrowserPosition(opts);
}

export async function getBaiduLocalCity(opts = {}) {
  const pos = await getBaiduBrowserPosition(opts);
  const city = pos?.address?.city || pos?.address?.province || '';
  if (!city && pos?.latitude == null) {
    throw Object.assign(new Error('未能获取城市中心坐标'), {
      code: 'UNAVAILABLE',
      permissionDenied: false,
    });
  }
  return {
    ...pos,
    address: pos.address || {
      province: '',
      city,
      district: '',
      township: '',
      street: '',
      streetNumber: '',
      formattedAddress: city,
      source: '浏览器定位',
    },
    source: 'localcity',
  };
}
